import { groupBy, values } from 'underscore';
import { ProductType } from '@ve/types/ProductType';
import type Backbone from 'backbone';
import type { IBackboneProduct } from '@ve/types/backboneModels/Domain/ObjectValue';
import groupProductsByCriterias from '../../groupProductsByCriterias';

export const TOP_BLOCKS_LIMIT = 20;

interface TopBlockOperator {
    operator_id: number;
    min_price: number;
    min_oil_tax: number;
}

interface TopBlock {
    room_name: string | null;
    meal_id: number;
    beds: string | null;
    nightsInHotel: number,
    nights: number,
    room_standart_name: string | null,
    operators: TopBlockOperator[];
}

interface TopBlocksPerDate {
    departure_date: string;
    blocks: TopBlock[];
}

const groupValues = <T, K extends string | number>(items: T[], keyOf: (item: T) => K): T[][] =>
    values(groupBy(items, keyOf));

const isTourProduct = (tour: IBackboneProduct): boolean =>
    tour.get('type') === ProductType.TourProduct;

const getProductModel = (tour: IBackboneProduct) => tour.get('product');

const getRoomName = (tour: IBackboneProduct): string | null => {
    const room = getProductModel(tour)?.get('room');
    return room?.name || null;
};

const getRoomStandartName = (tour: IBackboneProduct): string => {
    const room = getProductModel(tour)?.get('room');
    return room?.standardName || 'null';
};

const getNights = (tour: IBackboneProduct): number => {
    return getProductModel(tour)?.get('nights') || 0;
};

const getNightsInHotel = (tour: IBackboneProduct): number => {
    return getProductModel(tour)?.get('nightsInHotel') || 0;
};

const getBeds = (tour: IBackboneProduct): string =>
    getProductModel(tour)?.get('room')?.beds || 'null';

const getMealId = (tour: IBackboneProduct): number =>
    getProductModel(tour)?.get('meal')?.get('id');

const getOperatorId = (tour: IBackboneProduct): number =>
    getProductModel(tour)?.get('operator')?.id;

const getCheckInDate = (tour: IBackboneProduct): string =>
    getProductModel(tour)?.get('checkInDate');

const getPrice = (tour: IBackboneProduct): number =>
    getProductModel(tour)?.get('price');

const getOilTax = (tour: IBackboneProduct): number => {
    const priceWithOilTax = getProductModel(tour)?.get('priceWithOilTax') ?? 0;
    return priceWithOilTax - getPrice(tour);
};

const buildOperator = (toursOfSameOperator: IBackboneProduct[]): TopBlockOperator => {
    const [tour] = toursOfSameOperator;
    return {
        operator_id: getOperatorId(tour),
        min_price: getPrice(tour),
        min_oil_tax: getOilTax(tour),
    };
};

const buildBlock = (toursOfSameBlock: IBackboneProduct[]): TopBlock => {
    const [tour] = toursOfSameBlock;
    return {
        room_name: getRoomName(tour),
        meal_id: getMealId(tour),
        beds: getBeds(tour),
        nights: getNights(tour),
        nightsInHotel: getNightsInHotel(tour),
        room_standart_name: getRoomStandartName(tour),
        operators: groupValues(toursOfSameBlock, getOperatorId).map((group) => buildOperator(group)),
    };
};

const buildEntryForDate = (toursOfSameDate: IBackboneProduct[]): TopBlocksPerDate => ({
    departure_date: getCheckInDate(toursOfSameDate[0]),
    blocks: groupProductsByCriterias(toursOfSameDate)
        .slice(0, TOP_BLOCKS_LIMIT)
        .map((group) => buildBlock(group)),
});

const getTopBlocksPerDate = (
    foundTours?: Backbone.Collection<IBackboneProduct> | null,
): TopBlocksPerDate[] => {
    const tourProducts = foundTours?.filter(isTourProduct) ?? [];
    return groupValues(tourProducts, getCheckInDate).map((group) => buildEntryForDate(group));
};

export default getTopBlocksPerDate;